import * as React from "react";
import {SyntheticEvent} from "react";

import "./layer-list-item.scss";
import {Button, Checkbox, Radio} from "semantic-ui-react";

import {LayerListList} from "../layer-list-list";
import {Icon} from "../../../icon/icon";
import {UiState} from "../../../../ui-state";
import {LayerListConfiguration} from "../../../../config/layer-list-configuration";
import {MetadataConfiguration} from "../../../../config/metadata-configuration";
import {AppState} from "../../../../../components/app/app-mobx";
import {SidebarControlsConfiguration} from "../../../../../config/sidebar-configuration";
import {Legend} from "../../../legend/legend";
import {Legend as CustomLegend} from "../../../../../components/app/sidebar/legend/legend";
import {PathUtils} from "../../../../../utils/path-utils";
import WatchHandle = __esri.WatchHandle;
import _ from "lodash";
import {MapState} from "../../../../map-state";
import ListItem from "@arcgis/core/widgets/LayerList/ListItem";
import Collection from "@arcgis/core/core/Collection";
import {autorun} from "mobx";


export interface LayerListItemProps {
    config: LayerListConfiguration;
    subitems?: Collection<ListItem>;
    data: __esri.ListItem;
    metadata: MetadataConfiguration;
    isRoot: boolean;
    level: number;
    mapStore: MapState;
    onClick: (event: SyntheticEvent, data: object, item: __esri.ListItem) => any;
    view: __esri.MapView | __esri.SceneView | undefined;
    uiStore: UiState;
    appStore: AppState;
    controlsConfig: SidebarControlsConfiguration;
    small?: boolean;
}

export class LayerListItem extends React.Component<LayerListItemProps, any> {
    autorunDisposers: any[] = [];
    watchHandles: WatchHandle[] = [];
    metauid: string;
    constructor(props: LayerListItemProps) {
        super(props);

        this.state = {
            visible: props.data.layer.visible,
            visibleAtCurrentScale: props.data.visibleAtCurrentScale,
            open: props.data.open,
            metauid: null
        };
        this.handleLayerToggle = this.handleLayerToggle.bind(this);
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    }

    /**
     * Toggles visibility of sub layers
     */
    handleLayerToggle(): void {
        const {data} = this.props;
        this.setState((state: any) => {
            data.open = !state.open;
            return {
                open: !state.open
            };
        });

    }

    /**
     * Handle checkbox change
     *
     * @param event
     * @param eventData
     * @param item
     */
    handleCheckboxChange(event: SyntheticEvent, eventData: object, item: __esri.ListItem): void {
        event.preventDefault();
        // item is part of the top level state
        // --> call callback function to register changes on level of layer list
        if (this.props.onClick) {
            this.props.onClick(event, eventData, item);
        }

        const visible = eventData["checked"];
        // set internal visibility state
        this.setState({
            visible
        });
    }

    handleItemChange(data: __esri.ListItem): void {
        const {metadata} = this.props;
        if (!data || !data.layer) {
            return;
        }
        const metauid = data.layer.get("metauid");
        if (typeof metauid === "string") {
            this.setState({metauid});
        } else {
            const url = data.layer.get("url");
            // metadate Configuration überprüfen
            if (typeof url === "string") {
                const pathUid: string | null = PathUtils.getMetaId(url);
                if (pathUid) {
                    let uid: string | null = null;
                    const metadateConfig = metadata.metauid[pathUid];
                    if (typeof metadateConfig === "string") {
                        uid = metadateConfig;
                    } else if (Array.isArray(metadateConfig) || typeof metadateConfig === "object") {
                        let layerId: number | undefined | null = data.layer.get("layerId");
                        if (!layerId) {
                            layerId = PathUtils.getLayerId(url);
                        }
                        if (layerId && typeof metadateConfig[layerId] === "string") {
                            uid = metadateConfig[layerId];
                        }
                    }
                    if (uid === "") {
                        uid = null;
                    }
                    this.setState({metauid: uid});
                }
            } else {
                if (typeof metadata.metauid[data.title] !== "undefined") {
                    this.setState({metauid: metadata.metauid[data.title]});
                }
            }
        }
    }
    checkVisibility(): void {
        const {data, subitems} = this.props;
        let newValue = data.visibleAtCurrentScale;
        if(newValue && subitems?.length){
            newValue = subitems.findIndex((i) => i.visibleAtCurrentScale)>-1;
        }
        if(this.state.visibleAtCurrentScale !== newValue) {
            this.setState(() => ({
                visibleAtCurrentScale: newValue
            }));
        }
    }
    componentDidMount(): void {
        const {data} = this.props;
        this.handleItemChange(data);
        this.checkVisibility();
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((r: any) => {
            const {view} = this.props;
            if (!view) {
                return;
            }
            this.watchHandles.push(
                view.watch("scale",()=> {
                    this.checkVisibility();
                })
            );
            r.dispose();
        }));
    }

    componentWillUnmount(): void {
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        this.watchHandles.forEach((handle: WatchHandle) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
    }

    componentDidUpdate(prevProps: any) {
        const {data} = this.props;
        if (!_.isEqual(prevProps.data?.layer, data?.layer)){
            this.handleItemChange(data);
        }
        if (this.state.open !== data?.open){
            this.setState({open: data.open});
        }
        if (this.state.visible !== data?.layer.visible){
            this.setState({visible: data.layer.visible});
        }
    }

    render(): JSX.Element {
        const {visible, visibleAtCurrentScale, open, metauid} = this.state;
        const {subitems, data, metadata, onClick, config,
            controlsConfig, level, uiStore, appStore, mapStore, small, view}
            = this.props;
        const nextLevel = level + 1;
        const showElement = (controlsConfig.layerList.startLevel <= level);
        const nextIsRoot = (controlsConfig.layerList.startLevel === nextLevel);
        const limitLevel = controlsConfig.layerList.limitLevel;
        const hasChildren = subitems && subitems.length > 0 && (limitLevel === 0 || limitLevel > nextLevel)
            && data.layer.get("limitLevel") !== 1;
        const metaUrl = metauid != null? metadata.urlTemplate.replace("{METAUID}", this.state.metauid): null;
        const showLayers = hasChildren && open;
        const indeterminate = visible && subitems && subitems.flatten((ite: __esri.ListItem) =>
            ite.children
        ).some((child: __esri.ListItem) => !child.visible);
        const inputType = (data.parent?.layer.get("visibilityMode") === "exclusive")? "radio": "checkbox";
        // Das Property uid ist für ListItem nicht dokumentiert!
        // const itemUid = (data as any).uid;
        let css = "layer-list-item";
        css += visible ? " visible" : "";
        css += open ? " open" : "";
        css += hasChildren ? " has-children" : "";
        css += visibleAtCurrentScale ? "" : " not-visible-at-scale";
        let legend;
        if(subitems?.length && visibleAtCurrentScale){
            css += subitems.findIndex((i) => i.visibleAtCurrentScale)>-1 ? "" : " not-visible-at-scale";
        }
        if (controlsConfig.legend.position === "layer" && !hasChildren) {
            const legendConfig = controlsConfig.legend;
            if (controlsConfig.legend.useEsri && controlsConfig.legend.esriConfig) {
                legend = (<Legend
                    uiStore={uiStore}
                    view={view}
                    config={controlsConfig.legend.esriConfig}
                    controlsConfig={controlsConfig}
                    appStore={appStore}
                    mapStore={mapStore}
                    keyAppend={data.layer.id}
                    layerInfos={[{layer: data.layer, title: data.title}]}
                    small={small as boolean}/>);
            } else {
                legend = (<CustomLegend
                    appStore={appStore}
                    uiStore={uiStore}
                    config={legendConfig}/>);
            }
        }
        if (showElement) {
            return (
                <li className={css}>
                    <div className="layer-list-item-container">
                        {hasChildren &&
                        <Button className="layer-list-item-toggle" icon basic size="tiny"
                            onClick={this.handleLayerToggle}>
                            <Icon name={open ? "navigate_minus" : "navigate_plus"} />
                        </Button>
                        }
                        {inputType==="radio"?
                            <Radio label={data.title}
                                checked={visible}
                                indeterminate={indeterminate}
                                onChange={(event: SyntheticEvent, eventData: object) => {
                                    this.handleCheckboxChange(event, eventData, data);
                                }} />:
                            <Checkbox label={data.title}
                                checked={visible}
                                indeterminate={indeterminate}
                                onChange={(event: SyntheticEvent, eventData: object) => {
                                    this.handleCheckboxChange(event, eventData, data);
                                }} />
                        }
                        {metaUrl &&
                        <Button
                            className="layer-list-item-meta" as={"a"}
                            href={metaUrl}
                            target={"_blank"}
                            icon basic size="medium" floated="right">
                            <Icon name="information" />
                        </Button>
                        }
                    </div>
                    {legend}
                    {showLayers &&
                    <LayerListList
                        listItems={(subitems)? subitems.toArray(): []}
                        onListItemClick={onClick}
                        isRoot={nextIsRoot}
                        level={nextLevel}
                        config={config}
                        mapStore={mapStore}
                        small={small as boolean}
                        uiStore={uiStore}
                        view={view}
                        controlsConfig={controlsConfig}
                        appStore={appStore} />
                    }
                </li>
            );
        } else {
            return (<LayerListList
                listItems={subitems? subitems.toArray(): []}
                onListItemClick={onClick}
                isRoot={nextIsRoot}
                level={nextLevel}
                config={config}
                mapStore={mapStore}
                small={small as boolean}
                uiStore={uiStore}
                view={view}
                controlsConfig={controlsConfig}
                appStore={appStore} />);
        }
    }
}
